import { useEffect, useRef, type ReactNode } from 'react';
import { useHistoryDismiss } from './use-history-dismiss.js';
import { lockBodyScroll } from '../platform/scroll-lock.js';
import { trapTab } from '../platform/trap-tab.js';

/**
 * THE ONE BOTTOM-SHEET CHROME (frames 3b/3d/3f, S2 — the overlay rebuild).
 *
 * A scrim over the page and a panel rising from the bottom edge: a 36×4 grabber, 14px top
 * corners, the `--surface-overlay` plane a step above the page, `--shadow-sheet`, and a foot
 * that clears the safe-area inset. Every sheet that is ONE act — a note, the acts list, a
 * confirm — wears this. It is deliberately NOT worn by the multi-step flows that own the whole
 * screen while they run (bulk edit, the booking form): the redesign frames draw those as
 * full-bleed, and "one sheet idiom per era" is about the single-act sheets that used to be
 * three competing looks (the fullscreen `.sheet`, the fullscreen `.acts-sheet`, the phone
 * `.picker`), not about collapsing every overlay into one shape.
 *
 * DISMISSAL IS THE SHEET'S JOB, IN ONE PLACE — Escape and a tap on the scrim, here. That
 * single seam is why this is a component and not a pile of classes: S2 part 3 (the Android
 * back-gesture / history entry) lands here once, and every sheet inherits it. The scrim is a
 * plain sibling of the panel (the proven `.picker-backdrop` + `.picker` structure), so
 * `aria-modal` on the panel — not `aria-hidden` on an ancestor — is what makes the page
 * behind it inert to assistive tech.
 */
export function Sheet({
  label,
  onClose,
  className,
  grabber = true,
  elevated = false,
  role = 'dialog',
  children,
}: {
  /** Names the dialog for assistive tech and for the e2e `getByRole('dialog', { name })`. */
  label: string;
  onClose: () => void;
  /** Extra class on the panel, for a sheet that needs its own body layout. */
  className?: string | undefined;
  /** The 36×4 grab handle. On by default; the Confirm (frame 3f) omits it — a decision to
   * make, not a sheet to flick away. */
  grabber?: boolean;
  /** Raises scrim + panel above an already-open sheet (the picker sits at z 11). The Confirm
   * opened from inside a picker needs this; a plain screen-level sheet does not. */
  elevated?: boolean;
  /** `alertdialog` for a Confirm — it interrupts for a decision; `dialog` for the rest. */
  role?: 'dialog' | 'alertdialog';
  children: ReactNode;
}): JSX.Element {
  const panel = useRef<HTMLDivElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);

  // Back / edge-swipe closes this sheet instead of popping the route (S2 part 3).
  useHistoryDismiss(onClose);

  useEffect(() => {
    // Where focus was before the sheet opened — restored on close so the trigger is not lost.
    returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Fallback focus target. A child that autofocuses a field (NoteSheet's textarea) runs its
    // own effect AFTER this one — children commit before parents — so its focus wins; this only
    // catches the sheets that have nothing to focus into (the acts list, a confirm).
    panel.current?.focus();
    // Lock the page: a scroll that reaches the scrim must not move the list underneath it.
    // ⚠️ THIS USED TO BE `document.body.style.overflow = 'hidden'` (until 0.1.395) — the exact
    // technique nav.tsx had already rejected in 0.1.368, and the one iOS Safari ignores, so a
    // scroll started over the scrim moved the list behind the sheet on a phone. The shared helper
    // is the fixed-body lock, and it reference-counts for the Confirm that opens inside a picker.
    const release = lockBodyScroll();
    return () => {
      release();
      returnTo.current?.focus?.();
    };
  }, []);

  const panelClass = ['sheet-panel', elevated ? 'sheet-top' : null, className]
    .filter((c) => c !== null && c !== undefined)
    .join(' ');

  return (
    <>
      <div className={elevated ? 'sheet-scrim sheet-top' : 'sheet-scrim'} onClick={onClose} />
      <div
        ref={panel}
        className={panelClass}
        role={role}
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        // A tap inside the panel is not a tap on the scrim: stop it before the scrim's onClose.
        onClick={(e) => {
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
            return;
          }
          // TAB STAYS INSIDE (0.1.507) — the shared walk, see `platform/trap-tab.ts`.
          trapTab(e, panel.current);
        }}
      >
        {grabber && <span className="sheet-grabber" aria-hidden="true" />}
        {children}
      </div>
    </>
  );
}
